import type { ReactNode } from 'react';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { agentInstallCommands } from '@cp/shared';
import { Accordion, Banner, CopyField } from '@/components/ui';
import { formatDateTime } from '@/utils/format';
import type { EnrollmentIssued } from '@/types/api';

/**
 * What the operator takes to the new server: the one-time token and the two
 * commands that use it. Shown once — only the token's hash is kept — and
 * written so they can be pasted as they are.
 *
 * The installer sets the agent up as a systemd service: an agent started by
 * hand stops managing the server the first time it reboots.
 */
export function EnrollmentInstructions({ issued }: { readonly issued: EnrollmentIssued }): ReactNode {
  const host = issued.role === 'host';
  const { download, install } = agentInstallCommands(issued);

  // For a development machine without systemd: the same agent, run by hand.
  const manual = [
    `curl -fsSL ${issued.bundleUrl} -o cp-agent.mjs`,
    `AGENT_API_URL=${issued.apiUrl} \\`,
    `AGENT_ENROLLMENT_TOKEN=${issued.token} \\`,
    host ? 'AGENT_MODE=host \\' : 'AGENT_EDGE_BIND=0.0.0.0 AGENT_EDGE_HTTP_PORT=80 AGENT_EDGE_HTTPS_PORT=443 \\',
    'node cp-agent.mjs',
  ].join('\n');

  return (
    <div className="stack">
      <Banner tone="warning" icon={KeyRound}>
        Токен показывается один раз и действует до {formatDateTime(issued.expiresAt)}.
      </Banner>

      <ol className="enroll-steps">
        <li>
          <span className="enroll-step-title">Подготовьте сервер</span>
          {host ? (
            <span className="field-hint">
              Нужны systemd и доступ к {issued.apiUrl}. Node.js 22 устанавливается автоматически; сервер не изменяется.
            </span>
          ) : (
            <span className="field-hint">
              Ubuntu 22.04 или 24.04, свободные порты 80 и 443, доступ к {issued.apiUrl}. Docker и Node.js 22 устанавливаются
              автоматически.
            </span>
          )}
        </li>
        <li>
          <span className="enroll-step-title">Скачайте установщик</span>
          <CopyField label="Команда" value={download} multiline />
        </li>
        <li>
          <span className="enroll-step-title">Запустите его с токеном</span>
          <CopyField label="Команда" value={install} multiline />
          {host ? null : (
            <span className="field-hint">
              <span className="mono">--cloudflare-only</span> открывает порты 80 и 443 только для Cloudflare.
            </span>
          )}
        </li>
        <li>
          <span className="enroll-step-title">Дождитесь подключения</span>
          <span className="field-hint">Состояние сервера сменится на «Активен».</span>
        </li>
      </ol>

      <Accordion title="Запуск без systemd">
        <div className="stack stack-tight">
          <CopyField label="Команды" value={manual} multiline />
          {issued.bundleSha256 ? (
            <span className="field-hint">
              SHA-256: <span className="mono">{issued.bundleSha256}</span>
            </span>
          ) : null}
        </div>
      </Accordion>

      <Banner tone="info" icon={ShieldAlert}>
        {host
          ? 'Агент не принимает входящих соединений и не изменяет сервер.'
          : 'Агент не принимает входящих соединений и не затрагивает контейнеры, созданные не им.'}
      </Banner>
    </div>
  );
}
